/**
 * VehicleMapPage — Mapa de Veículos (embed do sistema W2SAT em iframe)
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-25 v2.0.0 - Novo fluxo de autenticação em 2 etapas (popup de login
 *            no domínio do fornecedor + iframe montado somente após o login), conforme
 *            implementação de referência enviada pelo fornecedor. Remove o atributo
 *            `sandbox` (impedia o reaproveitamento do cookie de sessão) e adiciona
 *            persistência leve do estado logado em sessionStorage.
 *   RELEASE: 2026-08-14 v1.0.2 - Ajuste da URL de embed para app.w2sat.com.br (teste).
 *   RELEASE: 2026-08-13 v1.0.1 - Alteração da URL de embed para uaizychat.w2sat.com.br.
 *   RELEASE: 2026-08-12 v1.0.0 - Versão inicial: página de teste que embeda o
 *            sistema do fornecedor (W2Sat) via iframe.
 *
 * FLUXO:
 *   1) Usuário clica em "Fazer login no painel" → abre popup (contexto primário) na URL
 *      do fornecedor. O cookie de sessão é gravado no domínio do provedor sem restrição
 *      de cookie de terceiros.
 *   2) Um watcher (500 ms) detecta o fechamento do popup → estado passa para "logado".
 *   3) Somente então o iframe é montado, reaproveitando a sessão já autenticada.
 *
 * OBS: o reaproveitamento da sessão dentro do iframe depende do fornecedor enviar o
 * cookie com SameSite=None; Secure e liberar `frame-ancestors` para o domínio do Uaizy.
 * A tela trata esse cenário com aviso e fallback "Abrir em nova aba".
 */
import React from 'react';
import { AlertTriangle, ExternalLink, Loader2, LogIn, LogOut, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { InlineNotice } from '@/components/ui/inline-notice';
import { createLogger } from '@/lib/logger';

// Logger com namespace dedicado da página (padrão do sistema — sem console.log direto)
const pageLogger = createLogger('VehicleMap');

/**
 * URL do sistema externo liberada pelo fornecedor para embed.
 * Ambiente de TESTE do fornecedor. Para produção, trocar por: https://app.w2sat.com.br
 */
const TARGET_URL = 'https://w2sat-w2sat-tst.zlpspr.easypanel.host';

/** Tempo (ms) sem evento onLoad após o qual exibimos o aviso de possível bloqueio */
const LOAD_TIMEOUT_MS = 12000;

/** Intervalo (ms) de verificação do fechamento do popup de login */
const POPUP_WATCH_INTERVAL_MS = 500;

/** Dimensões do popup de login */
const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;

/** Chave de persistência leve do estado logado (escopo da aba) */
const SESSION_FLAG_KEY = 'w2sat:vehicle-map:logged';

export default function VehicleMapPage() {
  // Estado do fluxo de autenticação
  const [isLogged, setIsLogged] = React.useState<boolean>(() => {
    try {
      return window.sessionStorage.getItem(SESSION_FLAG_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [isOpeningLogin, setIsOpeningLogin] = React.useState(false);
  const [popupBlocked, setPopupBlocked] = React.useState(false);

  // Controle de carregamento / possível bloqueio do embed
  const [isLoading, setIsLoading] = React.useState(true);
  const [maybeBlocked, setMaybeBlocked] = React.useState(false);
  // Alterar a key força a remontagem do iframe (botão "Recarregar")
  const [reloadKey, setReloadKey] = React.useState(0);

  // Referências do popup e do watcher
  const popupRef = React.useRef<Window | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  /** Encerra o watcher e descarta a referência do popup */
  const stopPopupWatcher = React.useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    popupRef.current = null;
  }, []);

  // Limpeza obrigatória no unmount (evita timers/janelas órfãs)
  React.useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  // Timeout de detecção de bloqueio do embed — só roda com o iframe montado
  React.useEffect(() => {
    if (!isLogged) return;

    setIsLoading(true);
    setMaybeBlocked(false);

    // O navegador não expõe erro quando o frame é bloqueado; usamos timeout
    const timer = window.setTimeout(() => {
      setIsLoading((loading) => {
        if (loading) {
          pageLogger.warn('iframe não sinalizou onLoad no tempo esperado', { url: TARGET_URL });
          setMaybeBlocked(true);
        }
        return loading;
      });
    }, LOAD_TIMEOUT_MS);

    return () => window.clearTimeout(timer);
  }, [reloadKey, isLogged]);

  /** Marca o iframe como carregado com sucesso */
  const handleLoad = React.useCallback(() => {
    pageLogger.debug('iframe carregado', { url: TARGET_URL });
    setIsLoading(false);
    setMaybeBlocked(false);
  }, []);

  /** Abre o popup de login no domínio do fornecedor (contexto primário) */
  const handleOpenLogin = React.useCallback(() => {
    // Popup já aberto → apenas foca
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
      return;
    }

    setPopupBlocked(false);
    setIsOpeningLogin(true);

    // Centraliza a janela em relação à janela atual
    const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
    const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;

    const popup = window.open(
      TARGET_URL,
      'W2SAT_LOGIN',
      [
        `width=${POPUP_WIDTH}`,
        `height=${POPUP_HEIGHT}`,
        `left=${Math.max(0, Math.round(left))}`,
        `top=${Math.max(0, Math.round(top))}`,
        'resizable=yes',
        'scrollbars=yes',
      ].join(','),
    );

    // Popup bloqueado pelo navegador
    if (!popup) {
      pageLogger.warn('popup de login bloqueado pelo navegador', { url: TARGET_URL });
      setIsOpeningLogin(false);
      setPopupBlocked(true);
      return;
    }

    popupRef.current = popup;
    pageLogger.debug('popup de login aberto', { url: TARGET_URL });

    // Watcher: quando o usuário fechar o popup, consideramos o login concluído
    timerRef.current = setInterval(() => {
      if (popupRef.current && !popupRef.current.closed) return;

      pageLogger.debug('popup de login fechado — habilitando iframe');
      stopPopupWatcher();
      setIsOpeningLogin(false);
      setIsLogged(true);
      try {
        window.sessionStorage.setItem(SESSION_FLAG_KEY, '1');
      } catch {
        /* sessionStorage indisponível — segue apenas em memória */
      }
    }, POPUP_WATCH_INTERVAL_MS);
  }, [stopPopupWatcher]);

  /** Cancela o login em andamento (fecha popup e volta ao estado inicial) */
  const handleCancel = React.useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    stopPopupWatcher();
    setIsOpeningLogin(false);
  }, [stopPopupWatcher]);

  /** Sai do embed: desmonta o iframe e volta ao estado inicial */
  const handleLogout = React.useCallback(() => {
    handleCancel();
    setIsLogged(false);
    setMaybeBlocked(false);
    setIsLoading(true);
    try {
      window.sessionStorage.removeItem(SESSION_FLAG_KEY);
    } catch {
      /* sessionStorage indisponível — ignorar */
    }
  }, [handleCancel]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Cabeçalho compacto (padrão do sistema: sem gradientes/blur) */}
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <h1 className="truncate text-base font-semibold text-foreground">Mapa de Veículos</h1>
          <p className="truncate text-xs text-muted-foreground">
            {isLogged
              ? 'Rastreamento integrado — sessão ativa'
              : isOpeningLogin
                ? 'Aguardando conclusão do login na janela aberta'
                : 'Rastreamento integrado — autenticação necessária'}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {/* Login / Cancelar / Sair conforme o estado do fluxo */}
          {!isLogged && !isOpeningLogin && (
            <Button size="sm" onClick={handleOpenLogin}>
              <LogIn className="mr-2 h-4 w-4" />
              Fazer login no painel
            </Button>
          )}

          {isOpeningLogin && (
            <Button variant="outline" size="sm" onClick={handleCancel}>
              <X className="mr-2 h-4 w-4" />
              Cancelar
            </Button>
          )}

          {isLogged && (
            <>
              <Button variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Recarregar
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Sair
              </Button>
            </>
          )}

          <Button variant="outline" size="sm" asChild>
            <a href={TARGET_URL} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" />
              Abrir em nova aba
            </a>
          </Button>
        </div>
      </div>

      {/* Aviso de popup bloqueado pelo navegador */}
      {popupBlocked && (
        <div className="px-4 pt-3">
          <InlineNotice variant="warning">
            O navegador bloqueou a janela de login. Libere os pop-ups para este site e tente
            novamente, ou utilize "Abrir em nova aba" para autenticar.
          </InlineNotice>
        </div>
      )}

      {/* Área do embed */}
      <div className="relative min-h-0 flex-1 bg-muted/20">
        {/* Estado 1: aguardando o usuário iniciar o login */}
        {!isLogged && !isOpeningLogin && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <LogIn className="h-7 w-7 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">Aguardando autenticação</p>
            <p className="max-w-md text-sm text-muted-foreground">
              Clique em "Fazer login no painel" para autenticar sua conta no sistema de
              rastreamento. Após concluir, feche a janela para carregar o mapa aqui.
            </p>
            <Button size="sm" onClick={handleOpenLogin}>
              <LogIn className="mr-2 h-4 w-4" />
              Fazer login no painel
            </Button>
          </div>
        )}

        {/* Estado 2: login em andamento (popup aberto) */}
        {isOpeningLogin && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">Login em andamento</p>
            <p className="max-w-md text-sm text-muted-foreground">
              Faça o login na janela que foi aberta. Ao concluir, feche a janela para que o mapa
              seja carregado nesta tela.
            </p>
          </div>
        )}

        {/* Estado 3: logado — iframe montado */}
        {isLogged && (
          <>
            {/* Carregando o embed */}
            {isLoading && !maybeBlocked && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Carregando sistema externo...</span>
              </div>
            )}

            {/* Aviso de possível bloqueio do embed pelo fornecedor */}
            {maybeBlocked && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-background/95 px-6 text-center">
                <AlertTriangle className="h-7 w-7 text-warning" />
                <p className="max-w-md text-sm text-foreground">
                  Não foi possível confirmar o carregamento do sistema externo dentro do Uaizy.
                  Isso normalmente indica que o embed está bloqueado pelo fornecedor
                  (X-Frame-Options / CSP frame-ancestors) ou que a sessão não foi compartilhada
                  com o iframe.
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setReloadKey((k) => k + 1)}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Tentar novamente
                  </Button>
                  <Button size="sm" asChild>
                    <a href={TARGET_URL} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Abrir em nova aba
                    </a>
                  </Button>
                </div>
              </div>
            )}

            {/* IMPORTANTE: sem atributo `sandbox` — ele isolaria o contexto e impediria
                o reaproveitamento do cookie de sessão criado no popup de login. */}
            <iframe
              key={reloadKey}
              src={TARGET_URL}
              title="Mapa de Veículos"
              className="h-full w-full border-0"
              onLoad={handleLoad}
              allow="geolocation; fullscreen; clipboard-write"
            />
          </>
        )}
      </div>
    </div>
  );
}
